import { getDefaultClient } from '../../../../client/defaultClient';
import { assertDefined } from '../../../../utils/assertDefined';
import type { Chain } from '../../../chain';
import { getNetworkProviderFromNetworkId } from '../getNetworkProviderFromNetworkId';

type GetBalanceForAddressParams = {
  address: string;
  chain: Chain;
  networkId: string;
};

/**
 * Retrieves the native token balance for a specific address on a given network.
 *
 * This function queries the balance directly for any address without requiring
 * a connected wallet account, useful for checking balances of external addresses.
 *
 * @param params.address - The wallet address to check the balance for.
 * @param params.chain - The chain (e.g., 'EVM', 'SOL').
 * @param params.networkId - The specific network ID within the chain.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to an object containing the balance as a string or null.
 */
export const getBalanceForAddress = async (
  { address, chain, networkId }: GetBalanceForAddressParams,
  client = getDefaultClient()
): Promise<{ balance: string | null }> => {
  const networkProvider = getNetworkProviderFromNetworkId(
    { chain, networkId },
    client
  );

  assertDefined(
    networkProvider,
    `No network found for chain ${chain} and network id ${networkId}. Please ensure you've enabled this network in the Dashboard.`
  );

  return networkProvider.getBalance({ address });
};
